import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, HostBinding, Input, OnChanges, OnDestroy, OnInit, Optional, Output, signal, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ControlContainer, ControlValueAccessor, UntypedFormControl, UntypedFormGroup, NG_VALUE_ACCESSOR, ValidationErrors, Validators, NG_VALIDATORS, Validator, AbstractControl } from '@angular/forms';
import { Attachment, CodeCategory, CodeType, CustomFieldMappingEnriched, CustomFieldParent, CustomFieldType, CustomFieldUtility, DateUtility, Entity, Hyperlink, RegionalSettings, toLinkHref } from '@jms/model';
import { numberValidator } from '@jms/ui/ng2-validation/src/number';
import { JmsValidators } from '@jms/utils/util.validators';
import { PickerFetch } from '@jms/ui/ui.core.pickers';
import { FieldButton, FieldComponent, FieldEditMode } from '@jms/ui/ui.field';
import { NumberFieldOptions } from '@jms/ui/ui.form.engine';
import { PickerMode } from '@jms/ui/ui.pickers.base';
import * as Enumerable from 'linq';
import { DateTime } from 'luxon';
import { Subscription, BehaviorSubject } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { CustomFieldService } from '../../services';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { CustomFieldTypePipe } from '../../custom.field.type.pipe';
import { UploadAllowedType } from '@workbuddy/ui/fields/upload';
import { DeleteAttachmentArgs } from '@workbuddy/ui/fields/upload/services';
import { Clipboard } from '@angular/cdk/clipboard';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'custom-field-section',
    templateUrl: './custom.field.section.component.html',
    styleUrls: ['./custom.field.section.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomFieldSectionComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => CustomFieldSectionComponent),
            multi: true
        }
    ],
    standalone: false
})
export class CustomFieldSectionComponent implements OnInit, ControlValueAccessor, Validator, OnChanges, OnDestroy {

    @Input() public mappings: CustomFieldMappingEnriched[];
    @Input() public fetchMode: PickerFetch = PickerFetch.Manual;
    @Input() public useFields = false;

    @Input() public data: any;
    @Input() public editMode: FieldEditMode;
    @Input() public showLabels = true;

    @Input() public formControl: UntypedFormControl;
    @Input() public formControlName: string;

    @Input() public parent = CustomFieldParent.Job;
    @Input() public showCard = false;
    @Input() public readonly = false;

    // Hosts that stack several sections (item panel: Code + EmbeddedItem) own the empty state themselves,
    // otherwise an empty section prints "No custom fields available" under a sibling that has fields.
    @Input() public showEmptyMessage = true;

    //#region Filter

    // Default
    @Input() public jobTypeId: string;
    @Input() public enforceJobType = true;

    // for Code
    @Input() public itemType: CodeType;
    @Input() public itemCategory: CodeCategory;

    // Work Order
    @Input() public workCategoryId: string;

    // Project
    @Input() public projectType: string;

    // CustomFieldContactScope value ('BasicContact' | 'Client' | 'Subcontractor')
    @Input() public contactType: string;

    @Input() public checkShowOnNew: boolean;

    //#endregion

    @Output() public edit = new EventEmitter<Entity>();

    @HostBinding('class.fieldset') fieldset = true;

    public CustomFieldType = CustomFieldType;
    public PickerMode = PickerMode;
    public form: UntypedFormGroup;
    public orderedMappings$ = new BehaviorSubject<CustomFieldMappingEnriched[]>([]);
    public readonly $isEmpty = signal(true);

    private formSubscription: Subscription;
    private valueInitialized = false;
    private _defaultDate: DateTime;
    public tenantRegion: RegionalSettings;
    private typePipe = new CustomFieldTypePipe();
    protected UploadAllowedType = UploadAllowedType;

    private imageFormMap = new Map<string, UntypedFormControl>();
    protected timezoneMap = new Map<string, string>();
    protected $editingLink = signal<{ id: string, data: Hyperlink }>(null);

    constructor(
        @Optional() private controlContainer: ControlContainer,
        private cdr: ChangeDetectorRef,
        private customFieldService: CustomFieldService,
        private generalSettingsService: GeneralSettingService,
        private clipboard: Clipboard,
        @Optional() private messageService: MessageService,
    ) {
        const now = new Date().toISOString();
        const nowAsUtc = DateTime
            .fromISO(now)
            .setZone(DateUtility.getLocalTimeZone())
            .setZone('utc', { keepLocalTime: true });
        this._defaultDate = nowAsUtc;
        this.tenantRegion = this.generalSettingsService.generalSettings?.region;
    }

    async ngOnInit() {


        if (this.fetchMode === PickerFetch.Automatic) {
            this.mappings = await this.customFieldService.getCustomFieldMapping(this.parent, this.checkShowOnNew === true);
            this.buildForm();
            this.cdr.markForCheck();
        }

    }

    private get parentControl() {
        if (this.formControl)
            return this.formControl;
        if (this.formControlName) {
            const control = this.controlContainer.control.get(this.formControlName.toString()) as UntypedFormControl;
            return control;
        }
        return null;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.fetchMode === PickerFetch.Automatic && !this.mappings) return;
        if (changes.data) {
            this.buildForm(true);
        }
        else {
            this.buildForm();
        }
    }

    ngOnDestroy() {
        if (this.formSubscription)
            this.formSubscription.unsubscribe();
    }

    public shouldShowLabels(mapping: CustomFieldMappingEnriched): boolean {
        if (this.showLabels) return true;
        if (CustomFieldUtility.getFieldType(mapping.fieldId) === CustomFieldType.Boolean) return true;

        return false;
    }

    public get defaultDate(): DateTime {
        return this._defaultDate;
    }

    protected getFormControl(fieldId: string): UntypedFormControl {
        if (!this.imageFormMap.has(fieldId)) {
            this.imageFormMap.set(fieldId, new UntypedFormControl(null));
        }

        return this.imageFormMap.get(fieldId);
    }

    protected attachmentUploaded(fieldId: string, attachment: any) {
        const images = this.imageFormMap.get(fieldId).value as Attachment[];
        if (!images) {
            return;
        }

        const imagesOnly = images.map(e => e.path)
        if (imagesOnly.length > 0) {
            this.form.get(fieldId).patchValue(imagesOnly[0]);
            this.edit.emit({ _id: fieldId, name: attachment?._id });
        }
    }

    protected onImageRemoved(fieldId: string) {

        const images = this.imageFormMap.get(fieldId)?.value as Attachment[];
        if (!images?.length) {
            this.form.get(fieldId)?.patchValue(null);
        }
    }

    protected async attachmentDeleted(fieldId: string, deleteFile: DeleteAttachmentArgs) {
        const images = this.imageFormMap.get(fieldId).value as Attachment[];
        const id = deleteFile.attachment._id;

        if (!images?.length) {
            this.form.get(fieldId).patchValue(null);
            this.edit.emit({ _id: fieldId, name: id });
            return;
        }

        const remaining = images.filter(x => x._id !== id);
        const imagesOnly = remaining.map(e => e.path)
        if (!imagesOnly.length) {
            this.form.get(fieldId).patchValue(null);
            this.edit.emit({ _id: fieldId, name: id });
            return;
        }

        this.form.get(fieldId).patchValue(imagesOnly[0]);
        this.edit.emit({ _id: fieldId, name: id });
    }

    private buildForm(dataChanged = false) {
        let currentValue = {};
        if (!dataChanged) {
            currentValue = this.form?.value ?? {};
        }

        const safeData = { ...currentValue, ...(this.data ?? {}) };
        const mappingMap = {};
        const orderedMappings = Enumerable
            .from(this.mappings)
            .where(x => {
                const meet = this.isMeetTheConditions(x);
                if (!meet) {
                    delete safeData[x.fieldId];
                }

                return meet;
            })
            .select(mapping => {
                safeData[mapping.fieldId] = safeData[mapping.fieldId] ?? mapping.defaultValue;
                mappingMap[mapping.fieldId] = mapping;
                return mapping;
            })
            .orderBy(x => x.sortOrder)
            .toArray();


        const keys = Object.keys(safeData);
        const form = new UntypedFormGroup({});

        this.imageFormMap.clear();

        Enumerable.from(keys)
            .forEach(key => {
                const mapping = mappingMap[key] as CustomFieldMappingEnriched;
                if (!mapping) return;

                const type = CustomFieldUtility.getFieldType(mapping.fieldId);

                const data = safeData[key];
                let validators = mapping.required ? [Validators.required] : [];

                switch (type) {

                    case CustomFieldType.Boolean:
                        // Ensure default value of false rather than null
                        safeData[key] = data ? !!data : false;
                        validators = [];
                        break;

                    case CustomFieldType.Date:
                        break;
                    case CustomFieldType.DateTime:
                        // data = data ? data : DateTime.fromJSDate(new Date()).setZone('utc');
                        const zone = DateUtility.getZoneFormIsoString(data?.toString());
                        this.timezoneMap.set(key, zone);

                        break;

                    case CustomFieldType.Email:
                        // Validators.email accepts dot-less domains (e.g. user@2608); use the same
                        // strict pattern as the entity form's Email field editor for consistency.
                        validators = [...validators, Validators.pattern(JmsValidators.getEmailPattern())];
                        break;

                    case CustomFieldType.Phone:
                        // Align with built-in phone fields: allow digits, spaces, (), +, -
                        validators = [...validators, Validators.pattern(JmsValidators.getPhonePattern())];
                        break;

                    case CustomFieldType.Number:
                        const options = mapping?.field?.options as NumberFieldOptions;
                        validators = [...validators, numberValidator];

                        const min = options?.min ? Validators.min(options.min) : null;
                        const max = options?.max ? Validators.max(options.max) : null;

                        if (min)
                            validators.push(min);

                        if (max)
                            validators.push(max);

                        break;
                    // case CustomFieldType.PickerEmployee:
                    //     break;
                }

                const control = new UntypedFormControl(data, validators);
                form.addControl(key, control);
                if (type === CustomFieldType.Image) {
                    this.imageFormMap.set(key, new UntypedFormControl(null));
                }
            });

        if (this.formSubscription) {
            this.formSubscription.unsubscribe();
            this.formSubscription = null;
        }

        this.form = form;

        this.setParentValidator();

        this.poplarImageField(safeData);
        form.patchValue(safeData);

        setTimeout(() => {

            form.markAsPristine();
        }, 1);


        this.formSubscription = this.form
            .valueChanges
            .pipe(
                filter(_ => this._onChange),
                tap(x => {

                    try {
                        this._onChange(x);
                    } catch { }

                    this.setParentValidator();
                })
            )
            .subscribe();

        if (this._onChange && (!this.valueInitialized || !this.hasSameValue(this.data, safeData))) {
            this.valueInitialized = true;
            this._onChange({ ...safeData });
        }
        this.onValidationChange();

        this.orderedMappings$.next(orderedMappings);
        this.$isEmpty.set(!orderedMappings.length);
    }

    private poplarImageField(data: any) {
        if (this.imageFormMap.size === 0) {
            return;
        }

        const keys = Array.from(this.imageFormMap.keys())

        for (let key of keys) {
            const image = data?.[key];
            if (!image) {
                // Clear stale attachment when the new data has no image for this fieldId.
                // imageFormMap lives outside this.form, so it is not reached by form.reset() —
                // without this clear, a previous entity's image bleeds into the new render.
                this.imageFormMap.get(key)?.patchValue(null, { emitEvent: false });
                continue;
            }

            const all = [];

            all.push({
                _id: image,
                path: image,
                type: 'Image'
            } as Attachment);

            this.imageFormMap.get(key).patchValue(all);
        }
    }

    private getFormErrors() {

        let allErrors = {};

        Enumerable
            .from(this.form.controls)
            .forEach(x => {
                const controlErrors = x?.value?.errors ?? {};

                Enumerable
                    .from(Object.keys(controlErrors))
                    .forEach(eKey => {
                        allErrors[`${x.key}:${eKey}`] = controlErrors[eKey];
                    });

            });

        if (!Object.keys(allErrors).length)
            allErrors = null;

        return allErrors;
    }

    private setParentValidator() {

        const parentControl = this.parentControl;
        if (parentControl) {

            // if (this.form.dirty)
            //     parentControl.markAsDirty();

            // if (this.form.pristine)
            //     parentControl.markAsPristine();

            // const errors = this.getFormErrors();
            // parentControl.setErrors(errors);

            parentControl.clearValidators();
            parentControl.setValidators(this.customFieldValidator.bind(this));
            parentControl.markAsPristine();
            parentControl.updateValueAndValidity();
            this.onValidationChange();

            // console.log('parentControl:', parentControl.invalid);
        }
    }

    public getButtons(field: string) {
        if (this.editMode === FieldEditMode.Default) return null;

        return [
            {
                label: 'Save',
                cssClass: 'btn-primary',
                disabled: () => {
                    return !this.form.controls[field].valid;
                }
            },
            {
                label: 'Cancel',
                isCancel: true
            }
        ] as FieldButton[];
    }

    public onButtonClick(field: string, button: any) {
        if (button.button.label === 'Save') {
            this.edit.emit({ _id: field, name: this.form.get(field).value });
            button.field.deactivate();
        }


    }

    public onCancelClick(field: string) {
        if (this.data)
            this.form.get(field)?.patchValue(this.data[field]);
    }

    public getType(mapping: CustomFieldMappingEnriched) {
        return this.typePipe.transform(mapping.fieldId);
    }

    protected startEditLink(fieldId: string): void {
        if (this.editMode === FieldEditMode.Default) {
            const editLink = this.$editingLink();
            if (editLink?.id) {
                this.cancelLink(editLink.id);
            }
        }
        else {
            FieldComponent.deactivateAllFields();
        }

        this.$editingLink.set({ id: fieldId, data: this.form.get(fieldId)?.value });
    }

    protected saveLink(fieldId: string): void {
        if (!this.form.get(fieldId)?.valid) {
            return;
        }

        this.edit.emit({ _id: fieldId, name: this.form.get(fieldId).value });
        this.$editingLink.set(null);
    }

    protected cancelLink(fieldId: string): void {
        this.form.get(fieldId)?.patchValue(this.$editingLink()?.data);
        this.$editingLink.set(null);
    }

    protected copyLink(url: string): void {
        // Copy the normalized (absolute) url so the pasted value is a working link.
        const copied = this.clipboard.copy(toLinkHref(url));
        if (copied) {
            this.messageService?.add({ severity: 'success', summary: 'Copied', detail: 'Link copied to the clipboard' });
        }
    }

    private customFieldValidator(control: UntypedFormControl): ValidationErrors | null {
        if (this.form.valid) return null;
        return this.getFormErrors();
    }

    private hasSameValue(left: Record<string, unknown> | null | undefined, right: Record<string, unknown>): boolean {
        const leftValue = left ?? {};
        const leftKeys = Object.keys(leftValue);
        const rightKeys = Object.keys(right);
        return leftKeys.length === rightKeys.length && leftKeys.every(key => leftValue[key] === right[key]);
    }

    private isMeetTheConditions(item: CustomFieldMappingEnriched): boolean {
        if (this.checkShowOnNew === true && !item.showOnNew) {
            return false;
        }

        let meetConditions = false;
        switch (item.parent) {
            case CustomFieldParent.Code:

                const hasItemTypeCond = !!item.itemTypes?.length;
                const hasItemCatCond = !!item.codeCategories?.length;

                const meetItemTypeCode = !hasItemTypeCond || (this.itemType && Enumerable.from(item.itemTypes).any(x => x === this.itemType));
                //Need to handle the all category - this will have a null subcategory, so we just check the category, otherwise we check the subcategory
                const meetItemCatCond = !hasItemCatCond ||
                    (this.itemCategory &&
                        Enumerable.from(item.codeCategories).any(x => (x.subCategoryId === this.itemCategory.subCategoryId) || (x.subCategoryId == null && x.categoryId == this.itemCategory.categoryId)));

                meetConditions = meetItemTypeCode && meetItemCatCond;
                break;
            case CustomFieldParent.WorkOrder:
                {
                    const haveJobTypeConditions = !!item.jobTypes?.length;
                    const haveWorkCategoryCondition = !!item.workCategories?.length;

                    const meetJobType = !this.enforceJobType || (!haveJobTypeConditions || (this.jobTypeId && Enumerable.from(item.jobTypes).any(x => x._id === this.jobTypeId)))
                    const meetWorkCategory = !haveWorkCategoryCondition || (this.workCategoryId && Enumerable.from(item.workCategories).any(x => x._id === this.workCategoryId))

                    meetConditions = meetJobType && meetWorkCategory;
                    break;
                }
            case CustomFieldParent.Project:
                {
                    const haveProjectTags = !!item.projectType?.length;
                    const meetProjectTags = haveProjectTags && this.projectType && Enumerable.from(item.projectType).any(x => x._id === this.projectType)

                    meetConditions = meetProjectTags;
                    break;
                }
            case CustomFieldParent.Contact:
                {
                    // Empty contactTypes → visible for all contact types.
                    const haveContactScope = !!item.contactTypes?.length;
                    if (!haveContactScope) {
                        meetConditions = true;
                    } else {
                        meetConditions = !!this.contactType
                            && Enumerable.from(item.contactTypes).any(x => x._id === this.contactType);
                    }
                    break;
                }
            default:
                if (!this.enforceJobType) meetConditions = true;
                const haveJobTypeConditions = !!item.jobTypes?.length;
                if (!haveJobTypeConditions)
                    meetConditions = true;
                else {
                    meetConditions = this.jobTypeId && Enumerable.from(item.jobTypes).any(x => x._id === this.jobTypeId);
                }
                break;
        }

        return meetConditions;
    }

    //#region ControlValueAccessor

    writeValue(data: any): void {
        this.data = data;
        if (!this.form) {
            this.cdr.markForCheck();
            return;
        }
        this.form.reset();

        if (this.data) {
            this.form.patchValue(data);
            this.poplarImageField(data);
            this.form.markAsPristine();
        }

        this.cdr.markForCheck();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void { }
    setDisabledState?(isDisabled: boolean): void { }

    _onChange: any;
    onValidationChange = () => { };


    validate(control: AbstractControl): ValidationErrors | null {
        return !this.form || this.form.valid ? null : { invalidCustomFieldValue: true };
    }
    registerOnValidatorChange(fn: () => void): void {
        this.onValidationChange = fn;
    }

    //#endregion
}
